import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DataTable,
  ExportButtons,
  FilterBar,
  Pager,
  Pill,
  SearchInput,
  Section,
  SelectFilter,
  StatCard,
  StatusBadge,
  usePaged,
} from "@/components/admin/admin-kit";
import {
  admMpCategories,
  admMpDeleteReview,
  admMpDisputes,
  admMpEndPromotion,
  admMpOrders,
  admMpPromotions,
  admMpReviews,
  admMpSellers,
  admMpSetOrderStatus,
  admMpSetSellerStatus,
  admMpStats,
  admMpResolveDispute,
} from "@/lib/admin-enterprise.functions";
import { formatMoney, formatUsd } from "@/lib/behot";
import { privatePageMetadata } from "@/lib/seo";

export const Route = createFileRoute("/nc/live/adm/mp")({
  head: () =>
    privatePageMetadata(
      "/nc/live/adm/mp",
      "Marketplace ops | Control room",
      "Internal administration.",
    ),
  component: AdminMarketplaceOps,
});

const TABS = ["orders", "sellers", "disputes", "reviews", "promotions", "categories"] as const;

const ORDER_STATUSES = [
  "awaiting_payment",
  "funded",
  "awaiting_requirements",
  "active",
  "delivered",
  "in_review",
  "revision_requested",
  "disputed",
  "late",
  "completed",
  "safety_hold",
  "earnings_pending",
  "withdrawable",
  "paid_out",
  "cancelled",
  "refunded",
  "frozen",
] as const;

const SELLER_STATUSES = [
  "not_a_seller",
  "application_draft",
  "kyc_pending",
  "kyc_more_info_required",
  "kyc_rejected",
  "seller_approved",
  "seller_suspended",
] as const;

function AdminMarketplaceOps() {
  const [tab, setTab] = useState<(typeof TABS)[number]>("orders");
  const stats = useServerFn(admMpStats);
  const { data } = useQuery({ queryKey: ["adm-mp-stats"], queryFn: () => stats({ data: undefined }) });

  return (
    <div className="space-y-4">
      {data && (
        <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <StatCard label="Orders" value={data.orders} hint={`${data.activeOrders} active`} />
          <StatCard label="GMV" value={formatMoney(data.gmvCents)} hint={formatUsd(data.gmvCents)} tone="hot" />
          <StatCard label="Platform fees" value={formatMoney(data.feeCents)} tone="ok" />
          <StatCard label="Payable" value={formatMoney(data.payableCents)} />
          <StatCard
            label="Services"
            value={data.services}
            hint={`${data.pendingServices} pending`}
            tone={data.pendingServices ? "warn" : "default"}
          />
          <StatCard
            label="Disputes / KYC"
            value={`${data.openDisputes} / ${data.pendingKyc}`}
            tone={data.openDisputes ? "warn" : "default"}
          />
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        {TABS.map((t) => (
          <Pill key={t} active={tab === t} onClick={() => setTab(t)}>
            {t}
          </Pill>
        ))}
      </div>
      {tab === "orders" && <OrdersTab />}
      {tab === "sellers" && <SellersTab />}
      {tab === "disputes" && <DisputesTab />}
      {tab === "reviews" && <ReviewsTab />}
      {tab === "promotions" && <PromotionsTab />}
      {tab === "categories" && <CategoriesTab />}
    </div>
  );
}

/* -------------------------------- Orders -------------------------------- */

function OrdersTab() {
  const qc = useQueryClient();
  const list = useServerFn(admMpOrders);
  const setStatusFn = useServerFn(admMpSetOrderStatus);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");

  const { data = [], isLoading } = useQuery({
    queryKey: ["adm-mp-orders", status, search],
    queryFn: () =>
      list({ data: { ...(status !== "all" ? { status } : {}), ...(search ? { search } : {}) } }),
  });

  const mut = useMutation({
    mutationFn: (input: { orderId: string; status: string }) => setStatusFn({ data: input }),
    onSuccess: () => {
      toast.success("Order updated.");
      void qc.invalidateQueries({ queryKey: ["adm-mp-orders"] });
      void qc.invalidateQueries({ queryKey: ["adm-mp-stats"] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Update failed."),
  });

  const exportRows = data.map((o) => ({
    order: o.order_number ?? o.id,
    created: o.created_at.slice(0, 10),
    buyer: o.buyer,
    seller: o.seller,
    status: o.status,
    total_aed: (Number(o.total_cents ?? 0) / 100).toFixed(2),
    fee_aed: (Number(o.platform_fee_cents ?? 0) / 100).toFixed(2),
    net_aed: (Number(o.seller_net_cents ?? 0) / 100).toFixed(2),
  }));

  const paged = usePaged(data, 50);

  return (
    <Section
      title="Marketplace orders"
      description="Override any order state, unlock funds or force completion."
      actions={<ExportButtons rows={exportRows} filename="behot-mp-orders" title="Marketplace orders" />}
    >
      <FilterBar>
        <SearchInput value={search} onChange={setSearch} placeholder="Order number, buyer or seller" />
        <SelectFilter
          label="Status"
          value={status}
          onChange={setStatus}
          options={[{ value: "all", label: "All" }, ...ORDER_STATUSES.map((s) => ({ value: s, label: s }))]}
        />
      </FilterBar>
      {isLoading ? (
        <p className="py-6 text-sm text-muted-foreground">Loading…</p>
      ) : (
        <>
          <DataTable
            rows={paged.slice}
            rowKey={(r) => r.id}
            empty="No marketplace orders."
            columns={[
              {
                key: "order",
                header: "Order",
                sortValue: (r) => r.order_number ?? r.id,
                cell: (r) => (
                  <div>
                    <p className="font-mono text-xs">{r.order_number ?? r.id.slice(0, 8)}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(r.created_at).toLocaleDateString()}
                    </p>
                  </div>
                ),
              },
              { key: "buyer", header: "Buyer", sortValue: (r) => r.buyer, cell: (r) => r.buyer },
              { key: "seller", header: "Seller", sortValue: (r) => r.seller, cell: (r) => r.seller },
              {
                key: "total",
                header: "Total",
                sortValue: (r) => Number(r.total_cents ?? 0),
                cell: (r) => (
                  <div className="whitespace-nowrap">
                    <p>{formatMoney(Number(r.total_cents ?? 0))}</p>
                    <p className="text-xs text-muted-foreground">
                      fee {formatMoney(Number(r.platform_fee_cents ?? 0))}
                    </p>
                  </div>
                ),
              },
              {
                key: "status",
                header: "Status",
                sortValue: (r) => r.status,
                cell: (r) => (
                  <select
                    className="rounded-md border bg-background px-2 py-1 text-xs"
                    value={r.status}
                    onChange={(e) => mut.mutate({ orderId: r.id, status: e.target.value })}
                  >
                    {ORDER_STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                ),
              },
            ]}
          />
          <Pager {...paged} onPrev={paged.prev} onNext={paged.next} />
        </>
      )}
    </Section>
  );
}

/* -------------------------------- Sellers ------------------------------- */

function SellersTab() {
  const qc = useQueryClient();
  const list = useServerFn(admMpSellers);
  const setStatusFn = useServerFn(admMpSetSellerStatus);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");

  const { data = [], isLoading } = useQuery({
    queryKey: ["adm-mp-sellers", status, search],
    queryFn: () =>
      list({ data: { ...(status !== "all" ? { status } : {}), ...(search ? { search } : {}) } }),
  });

  const mut = useMutation({
    mutationFn: (input: { userId: string; status: string }) => setStatusFn({ data: input }),
    onSuccess: () => {
      toast.success("Seller updated.");
      void qc.invalidateQueries({ queryKey: ["adm-mp-sellers"] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Update failed."),
  });

  const exportRows = data.map((s) => ({
    seller: String(s["display_name"] ?? ""),
    handle: String(s["handle"] ?? ""),
    company: String(s["company_name"] ?? ""),
    licence: String(s["trade_licence_number"] ?? ""),
    country: String(s["country_code"] ?? ""),
    status: String(s["application_status"] ?? ""),
    completed_orders: Number(s["completed_orders"] ?? 0),
    earned_aed: (Number(s["total_earned_cents"] ?? 0) / 100).toFixed(2),
    rating: String(s["avg_rating"] ?? ""),
  }));

  const paged = usePaged(data, 50);

  return (
    <Section
      title="Sellers"
      description="Approve, suspend or re-open verification for any seller."
      actions={<ExportButtons rows={exportRows} filename="behot-mp-sellers" title="Sellers" />}
    >
      <FilterBar>
        <SearchInput value={search} onChange={setSearch} placeholder="Seller, handle or company" />
        <SelectFilter
          label="Status"
          value={status}
          onChange={setStatus}
          options={[{ value: "all", label: "All" }, ...SELLER_STATUSES.map((s) => ({ value: s, label: s }))]}
        />
      </FilterBar>
      {isLoading ? (
        <p className="py-6 text-sm text-muted-foreground">Loading…</p>
      ) : (
        <>
          <DataTable
            rows={paged.slice}
            rowKey={(r) => String(r["user_id"])}
            empty="No sellers."
            columns={[
              {
                key: "seller",
                header: "Seller",
                sortValue: (r) => String(r["display_name"] ?? ""),
                cell: (r) => (
                  <div>
                    <p className="font-medium">{String(r["display_name"] ?? "—")}</p>
                    <p className="text-xs text-muted-foreground">
                      {String(r["company_name"] ?? r["handle"] ?? "—")}
                    </p>
                  </div>
                ),
              },
              {
                key: "country",
                header: "Country",
                sortValue: (r) => String(r["country_code"] ?? ""),
                cell: (r) => String(r["country_code"] ?? "—"),
              },
              {
                key: "orders",
                header: "Completed",
                sortValue: (r) => Number(r["completed_orders"] ?? 0),
                cell: (r) => Number(r["completed_orders"] ?? 0),
              },
              {
                key: "earned",
                header: "Earned",
                sortValue: (r) => Number(r["total_earned_cents"] ?? 0),
                cell: (r) => formatMoney(Number(r["total_earned_cents"] ?? 0)),
              },
              {
                key: "rating",
                header: "Rating",
                sortValue: (r) => Number(r["avg_rating"] ?? 0),
                cell: (r) => (r["avg_rating"] ? Number(r["avg_rating"]).toFixed(2) : "—"),
              },
              {
                key: "status",
                header: "Verification",
                sortValue: (r) => String(r["application_status"] ?? ""),
                cell: (r) => (
                  <select
                    className="rounded-md border bg-background px-2 py-1 text-xs"
                    value={String(r["application_status"] ?? "not_a_seller")}
                    onChange={(e) =>
                      mut.mutate({ userId: String(r["user_id"]), status: e.target.value })
                    }
                  >
                    {SELLER_STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                ),
              },
            ]}
          />
          <Pager {...paged} onPrev={paged.prev} onNext={paged.next} />
        </>
      )}
    </Section>
  );
}

/* ------------------------------- Disputes ------------------------------- */

function DisputesTab() {
  const qc = useQueryClient();
  const list = useServerFn(admMpDisputes);
  const resolve = useServerFn(admMpResolveDispute);
  const [status, setStatus] = useState("all");
  const [note, setNote] = useState<Record<string, string>>({});

  const { data = [], isLoading } = useQuery({
    queryKey: ["adm-mp-disputes", status],
    queryFn: () => list({ data: status !== "all" ? { status } : {} }),
  });

  const mut = useMutation({
    mutationFn: (input: { disputeId: string; status: string; resolution?: string }) =>
      resolve({ data: input }),
    onSuccess: () => {
      toast.success("Dispute updated.");
      void qc.invalidateQueries({ queryKey: ["adm-mp-disputes"] });
      void qc.invalidateQueries({ queryKey: ["adm-mp-stats"] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Update failed."),
  });

  const exportRows = data.map((d) => ({
    id: String(d["id"]),
    order: String(d["order_id"] ?? ""),
    status: String(d["status"] ?? ""),
    reason: String(d["reason"] ?? ""),
    created: String(d["created_at"] ?? "").slice(0, 10),
  }));

  return (
    <Section
      title="Disputes"
      description="Resolve, refund or close buyer and seller disputes."
      actions={<ExportButtons rows={exportRows} filename="behot-mp-disputes" title="Disputes" />}
    >
      <FilterBar>
        <SelectFilter
          label="Status"
          value={status}
          onChange={setStatus}
          options={[
            { value: "all", label: "All" },
            { value: "open", label: "open" },
            { value: "under_review", label: "under_review" },
            { value: "resolved", label: "resolved" },
            { value: "refunded", label: "refunded" },
            { value: "closed", label: "closed" },
          ]}
        />
      </FilterBar>
      {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      <div className="space-y-2">
        {data.length === 0 && !isLoading && (
          <p className="py-6 text-center text-sm text-muted-foreground">No disputes.</p>
        )}
        {data.map((d) => {
          const id = String(d["id"]);
          return (
            <div key={id} className="card-surface p-4">
              <div className="flex flex-wrap items-start gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <StatusBadge status={String(d["status"] ?? "open")} />
                    <span className="font-mono text-xs text-muted-foreground">
                      order {String(d["order_id"] ?? "—").slice(0, 8)}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {new Date(String(d["created_at"])).toLocaleString()}
                    </span>
                  </div>
                  <p className="mt-2 text-sm">{String(d["reason"] ?? d["description"] ?? "—")}</p>
                </div>
                <div className="flex w-full flex-wrap items-center gap-2 md:w-auto">
                  <Input
                    placeholder="Resolution note"
                    value={note[id] ?? ""}
                    onChange={(e) => setNote((p) => ({ ...p, [id]: e.target.value }))}
                    className="max-w-[220px]"
                  />
                  {(["under_review", "resolved", "refunded", "closed"] as const).map((s) => (
                    <Button
                      key={s}
                      size="sm"
                      variant={s === "resolved" ? "hot" : "outline"}
                      onClick={() =>
                        mut.mutate({
                          disputeId: id,
                          status: s,
                          ...(note[id] ? { resolution: note[id] } : {}),
                        })
                      }
                    >
                      {s}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </Section>
  );
}

/* -------------------------------- Reviews ------------------------------- */

function ReviewsTab() {
  const qc = useQueryClient();
  const list = useServerFn(admMpReviews);
  const del = useServerFn(admMpDeleteReview);
  const [search, setSearch] = useState("");

  const { data = [], isLoading } = useQuery({
    queryKey: ["adm-mp-reviews"],
    queryFn: () => list({ data: {} }),
  });

  const mut = useMutation({
    mutationFn: (id: string) => del({ data: { id } }),
    onSuccess: () => {
      toast.success("Review removed.");
      void qc.invalidateQueries({ queryKey: ["adm-mp-reviews"] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Delete failed."),
  });

  const rows = data.filter((r) =>
    search.trim() ? JSON.stringify(r).toLowerCase().includes(search.trim().toLowerCase()) : true,
  );
  const exportRows = rows.map((r) => ({
    id: String(r["id"]),
    order: String(r["order_id"] ?? ""),
    rating: Number(r["rating"] ?? 0),
    comment: String(r["comment"] ?? ""),
    created: String(r["created_at"] ?? "").slice(0, 10),
  }));
  const paged = usePaged(rows, 50);

  return (
    <Section
      title="Reviews"
      description="Moderate marketplace feedback."
      actions={<ExportButtons rows={exportRows} filename="behot-mp-reviews" title="Reviews" />}
    >
      <FilterBar>
        <SearchInput value={search} onChange={setSearch} placeholder="Search reviews" />
      </FilterBar>
      {isLoading ? (
        <p className="py-6 text-sm text-muted-foreground">Loading…</p>
      ) : (
        <>
          <DataTable
            rows={paged.slice}
            rowKey={(r) => String(r["id"])}
            empty="No reviews."
            columns={[
              {
                key: "created",
                header: "Date",
                sortValue: (r) => String(r["created_at"] ?? ""),
                cell: (r) => new Date(String(r["created_at"])).toLocaleDateString(),
              },
              {
                key: "rating",
                header: "Rating",
                sortValue: (r) => Number(r["rating"] ?? 0),
                cell: (r) => `${Number(r["rating"] ?? 0)} / 5`,
              },
              {
                key: "comment",
                header: "Comment",
                cell: (r) => (
                  <span className="line-clamp-2 max-w-[520px] text-sm">
                    {String(r["comment"] ?? "—")}
                  </span>
                ),
              },
              {
                key: "actions",
                header: "",
                cell: (r) => (
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={mut.isPending}
                    onClick={() => mut.mutate(String(r["id"]))}
                  >
                    Remove
                  </Button>
                ),
              },
            ]}
          />
          <Pager {...paged} onPrev={paged.prev} onNext={paged.next} />
        </>
      )}
    </Section>
  );
}

/* ------------------------------ Promotions ------------------------------ */

function PromotionsTab() {
  const qc = useQueryClient();
  const list = useServerFn(admMpPromotions);
  const end = useServerFn(admMpEndPromotion);
  const { data = [], isLoading } = useQuery({
    queryKey: ["adm-mp-promotions"],
    queryFn: () => list({ data: {} }),
  });

  const mut = useMutation({
    mutationFn: (id: string) => end({ data: { id } }),
    onSuccess: () => {
      toast.success("Promotion ended.");
      void qc.invalidateQueries({ queryKey: ["adm-mp-promotions"] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Update failed."),
  });

  const exportRows = data.map((p) => ({
    id: String(p["id"]),
    service: String(p["service_id"] ?? ""),
    status: String(p["status"] ?? ""),
    starts: String(p["starts_at"] ?? "").slice(0, 10),
    ends: String(p["ends_at"] ?? "").slice(0, 10),
    heat: Number(p["price_cents"] ?? 0) / 100,
  }));

  return (
    <Section
      title="Featured promotions"
      description="Paid service featuring (5 days / 30 days)."
      actions={<ExportButtons rows={exportRows} filename="behot-mp-promotions" title="Promotions" />}
    >
      {isLoading ? (
        <p className="py-6 text-sm text-muted-foreground">Loading…</p>
      ) : (
        <DataTable
          rows={data}
          rowKey={(r) => String(r["id"])}
          empty="No promotions."
          columns={[
            {
              key: "service",
              header: "Service",
              cell: (r) => (
                <span className="font-mono text-xs">{String(r["service_id"] ?? "—").slice(0, 8)}</span>
              ),
            },
            {
              key: "window",
              header: "Window",
              sortValue: (r) => String(r["starts_at"] ?? ""),
              cell: (r) =>
                `${String(r["starts_at"] ?? "").slice(0, 10)} → ${String(r["ends_at"] ?? "").slice(0, 10)}`,
            },
            {
              key: "status",
              header: "Status",
              sortValue: (r) => String(r["status"] ?? ""),
              cell: (r) => <StatusBadge status={String(r["status"] ?? "—")} />,
            },
            {
              key: "actions",
              header: "",
              cell: (r) => (
                <Button size="sm" variant="outline" onClick={() => mut.mutate(String(r["id"]))}>
                  End now
                </Button>
              ),
            },
          ]}
        />
      )}
    </Section>
  );
}

/* ------------------------------ Categories ------------------------------ */

function CategoriesTab() {
  const list = useServerFn(admMpCategories);
  const { data = [], isLoading } = useQuery({
    queryKey: ["adm-mp-categories"],
    queryFn: () => list({ data: undefined }),
  });

  const exportRows = data.map((c) => ({
    id: String(c["id"]),
    name: String(c["name"] ?? ""),
    slug: String(c["slug"] ?? ""),
    parent: String(c["parent_id"] ?? ""),
    active: String(c["is_active"] ?? ""),
  }));

  return (
    <Section
      title="Marketplace categories"
      description="Catalogue structure used by the service wizard."
      actions={<ExportButtons rows={exportRows} filename="behot-mp-categories" title="Categories" />}
    >
      {isLoading ? (
        <p className="py-6 text-sm text-muted-foreground">Loading…</p>
      ) : (
        <DataTable
          rows={data}
          rowKey={(r) => String(r["id"])}
          empty="No categories."
          columns={[
            {
              key: "name",
              header: "Name",
              sortValue: (r) => String(r["name"] ?? ""),
              cell: (r) => String(r["name"] ?? "—"),
            },
            {
              key: "slug",
              header: "Slug",
              sortValue: (r) => String(r["slug"] ?? ""),
              cell: (r) => <span className="font-mono text-xs">{String(r["slug"] ?? "—")}</span>,
            },
            {
              key: "parent",
              header: "Parent",
              cell: (r) => (r["parent_id"] ? String(r["parent_id"]).slice(0, 8) : "top level"),
            },
            {
              key: "active",
              header: "Active",
              cell: (r) => <StatusBadge status={r["is_active"] ? "active" : "inactive"} />,
            },
          ]}
        />
      )}
    </Section>
  );
}
